import { useState, useEffect, useRef, useCallback } from 'react'
import { getActorMovies, posterUrl, profileUrl } from '../api/tmdb'
import {
  subscribeToGame,
  initCoopGame,
  submitCoopTurn,
  acknowledgeLastMove,
  abandonGame,
} from '../utils/firebaseGame'
import { updateSavedGame, getSavedGameById } from '../utils/savedGamesService'
import Filmstrip from './Filmstrip'
import { IconCut, IconTicket, IconClapper, IconTrophy, IconX, IconLock } from './icons'
import FlipMovieCard from './FlipMovieCard'
import FlipActorCard from './FlipActorCard'
import CardOverlay from './CardOverlay'

const ACTOR_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='185' height='278' viewBox='0 0 185 278'%3E%3Crect fill='%23333' width='185' height='278'/%3E%3Ccircle fill='%23555' cx='92' cy='100' r='40'/%3E%3Cellipse fill='%23555' cx='92' cy='220' rx='60' ry='50'/%3E%3C/svg%3E"
const MOVIE_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='92' height='138' viewBox='0 0 92 138'%3E%3Crect fill='%23333' width='92' height='138'/%3E%3C/svg%3E"

/**
 * Phase B — Co-Op turn-based gameplay.
 *
 * Props:
 *   gameId     – Firestore game doc ID
 *   playerKey  – 'p1' | 'p2'
 *   startActor – actor object (the shared starting point)
 *   goalActor  – actor object (what both players are aiming for)
 *   onExit     – called when the player exits (win screen or abandon)
 */
export default function CoopGameplay({ gameId, playerKey, startActor, goalActor, savedGameId, onExit, onRefundTicket }) {
  // ── Firestore live doc ───────────────────────────────────────────────────
  const [game, setGame] = useState(null)

  // ── Phase ────────────────────────────────────────────────────────────────
  // 'init' | 'reveal' | 'my-turn' | 'waiting' | 'won'
  const [phase, setPhase] = useState('init')

  // ── Movie / cast data ────────────────────────────────────────────────────
  // Step 1: pick movie. Step 2: pick actor from that movie's cast.
  // Step is derived: !selectedMovie → step 1, selectedMovie → step 2.
  const [movies, setMovies] = useState([])
  const [loadingMovies, setLoadingMovies] = useState(false)
  const [movieFilter, setMovieFilter] = useState('')
  const movieFilterRef = useRef(null)

  // selectedMovie and its cast are set together via the CardOverlay callback.
  const [selectedMovie, setSelectedMovie] = useState(null)
  const [cast, setCast] = useState([])

  // selectedActor is set via the actor CardOverlay callback.
  const [selectedActor, setSelectedActor] = useState(null)

  // ── Card overlay (zoom mechanic, same as single player) ──────────────────
  const [expandedCard, setExpandedCard] = useState(null) // { type, data, rect }

  // ── Submission / feedback ────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false)
  const [toast, setToast] = useState('')

  // ── Quit dialog ──────────────────────────────────────────────────────────
  const [quitDialogOpen, setQuitDialogOpen] = useState(false)

  // ── Abandoned by other player ────────────────────────────────────────────
  const [abandonedByOther, setAbandonedByOther] = useState(false)

  // ── Refs ─────────────────────────────────────────────────────────────────
  const acknowledgedTimestamp = useRef(null)
  const chainEndRef = useRef(null)
  // Tracks the actor ID for which movies are currently loaded / being fetched.
  const lastFetchedActorIdRef = useRef(null)
  // Cancels any in-flight getActorMovies request.
  const movieFetchCancelRef = useRef(null)
  // Ensures the opponent-abandon refund fires exactly once.
  const opponentAbandonRefundedRef = useRef(false)

  // ── P1 initialises currentTurn on mount ─────────────────────────────────
  useEffect(() => {
    if (playerKey === 'p1') {
      initCoopGame(gameId).catch(err =>
        console.error('[CoopGameplay] initCoopGame error:', err)
      )
    }
  }, [gameId, playerKey])

  // ── Real-time Firestore listener ─────────────────────────────────────────
  useEffect(() => {
    const unsub = subscribeToGame(gameId, (data) => {
      setGame(data)

      const { status, currentTurn, lastMove, abandonedBy } = data

      // Co-star called cut — refund our ticket (once) then show abandoned screen
      if (status === 'abandoned' && abandonedBy !== playerKey) {
        if (!opponentAbandonRefundedRef.current) {
          opponentAbandonRefundedRef.current = true
          onRefundTicket?.(1)
        }
        setAbandonedByOther(true)
        return
      }

      let nextPhase
      if (status === 'won') {
        nextPhase = 'won'
      } else if (!currentTurn) {
        nextPhase = 'init'
      } else if (currentTurn !== playerKey) {
        nextPhase = 'waiting'
      } else {
        const ts = lastMove?.timestamp ?? null
        nextPhase = (ts && ts !== acknowledgedTimestamp.current) ? 'reveal' : 'my-turn'
      }

      console.log(
        `[CoopGameplay] snapshot — currentTurn: ${currentTurn} | myKey: ${playerKey} | phase: ${nextPhase}`
      )

      setPhase(nextPhase)

      // ── On every my-turn snapshot, derive currentActor fresh from the
      //    snapshot data (never from stale component state) and trigger a
      //    fresh movie fetch if the actor has changed. ──────────────────────
      if (nextPhase === 'my-turn') {
        const freshChain = data.chain ?? []
        const freshLastActor = [...freshChain].reverse().find(i => i.type === 'actor')
        const freshCurrentActor = freshLastActor ?? startActor

        if (freshCurrentActor.id !== lastFetchedActorIdRef.current) {
          lastFetchedActorIdRef.current = freshCurrentActor.id
          console.log('[CoopGameplay] currentActor set to:', freshCurrentActor.name)

          // Reset all pick state before loading the new actor's movies
          setSelectedMovie(null)
          setCast([])
          setSelectedActor(null)
          setMovieFilter('')
          setExpandedCard(null)
          setMovies([])
          setLoadingMovies(true)
          window.scrollTo(0, 0)

          // Cancel any previous in-flight fetch
          if (movieFetchCancelRef.current) movieFetchCancelRef.current()
          let cancelled = false
          movieFetchCancelRef.current = () => { cancelled = true }

          getActorMovies(freshCurrentActor.id)
            .then(d => { if (!cancelled) setMovies(d ?? []) })
            .catch(() => {
              if (!cancelled) {
                setToast('Failed to load movies. Try again.')
                setTimeout(() => setToast(''), 3000)
              }
            })
            .finally(() => { if (!cancelled) setLoadingMovies(false) })
        }
      }
    })

    return () => {
      unsub()
      // Cancel any in-flight movie fetch on unmount
      if (movieFetchCancelRef.current) movieFetchCancelRef.current()
    }
  }, [gameId, playerKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-scroll chain end ────────────────────────────────────────────────
  useEffect(() => {
    chainEndRef.current?.scrollIntoView({
      behavior: 'smooth', block: 'nearest', inline: 'end',
    })
  }, [game?.chain?.length])

  // ── Derived: the actor that the active player browses from ───────────────
  // Derived from live game state for display only — movie fetching uses fresh
  // snapshot data inside the listener above.
  const chain = game?.chain ?? []
  const lastChainActor = [...chain].reverse().find(i => i.type === 'actor')
  const currentActor = lastChainActor ?? startActor

  // ── Helpers ──────────────────────────────────────────────────────────────
  const showToast = (msg) => {
    setToast(msg)
    setTimeout(() => setToast(''), 3000)
  }

  const handleAcknowledge = async () => {
    acknowledgedTimestamp.current = game?.lastMove?.timestamp
    // Optimistic local update — clear stale movies so the grid doesn't flash
    // old content. The Firestore snapshot will trigger the real fetch momentarily.
    setMovies([])
    setLoadingMovies(true)
    setPhase('my-turn')
    try { await acknowledgeLastMove(gameId) }
    catch (err) { console.error('[CoopGameplay] acknowledgeLastMove error:', err) }
  }

  // ── Quit ("Call Cut") handler ────────────────────────────────────────────
  const handleConfirmQuit = async () => {
    setQuitDialogOpen(false)
    try {
      await abandonGame(gameId, playerKey)
    } catch (err) {
      console.error('[CoopGameplay] abandonGame error:', err)
    }
    // Refund ticket if eligible
    if (savedGameId) {
      const entry = getSavedGameById(savedGameId)
      if (entry?.refundEligible) {
        onRefundTicket?.(entry.ticketSpent ?? 1)
      }
      updateSavedGame(savedGameId, { status: 'abandoned' })
    }
    onExit()
  }

  // ── Card overlay handlers (same mechanic as single-player) ───────────────
  const handleExpand = useCallback((type, data, rect) => {
    setExpandedCard({ type, data, rect })
  }, [])

  const handleOverlayDismiss = useCallback(() => {
    setExpandedCard(null)
  }, [])

  /**
   * Called by CardOverlay when the user confirms a selection.
   * - type 'movie': CardOverlay has already fetched the cast; store both.
   * - type 'actor': store the actor; lock-in bar will appear.
   */
  const handleOverlaySelect = useCallback((type, data, castData) => {
    setExpandedCard(null)
    if (type === 'movie') {
      setSelectedMovie(data)
      setCast(castData ?? [])
      setSelectedActor(null)
    } else {
      // actor selected — lock-in bar will appear
      setSelectedActor(data)
    }
  }, [])

  const handleBackToStep1 = () => {
    setSelectedMovie(null)
    setSelectedActor(null)
    setCast([])
    setMovieFilter('')
    setExpandedCard(null)
    window.scrollTo(0, 0)
  }

  const handleLockIn = async () => {
    if (!selectedMovie || !selectedActor || submitting) return
    setSubmitting(true)

    // Build entries that will be appended to the Firestore chain so we can
    // pre-compute the new hop count for the saved game update.
    const currentChain = game?.chain ?? []
    const movieEntry = {
      type: 'movie', id: selectedMovie.id, title: selectedMovie.title,
      poster_path: selectedMovie.poster_path ?? null,
    }
    const actorEntry = {
      type: 'actor', id: selectedActor.id, name: selectedActor.name,
      profile_path: selectedActor.profile_path ?? null, gender: selectedActor.gender ?? 0,
    }
    const newChain = [...currentChain, movieEntry, actorEntry]
    const newHops  = Math.floor(newChain.length / 2)
    const isWin    = selectedActor.id === goalActor.id

    try {
      await submitCoopTurn(gameId, playerKey, selectedMovie, selectedActor, goalActor.id)
      // Update the saved game entry with the new hop count and chain
      if (savedGameId) {
        updateSavedGame(savedGameId, {
          hops: newHops,
          chain: newChain,
          refundEligible: false,
          ...(isWin ? { status: 'completed' } : {}),
        })
      }
    } catch (err) {
      console.error('[CoopGameplay] submitCoopTurn error:', err)
      showToast('Something went wrong. Try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // ── Derived display data ─────────────────────────────────────────────────
  const displayChain = [
    {
      type: 'actor',
      id: startActor.id,
      name: startActor.name,
      profile_path: startActor.profile_path ?? null,
      gender: startActor.gender ?? 0,
    },
    ...chain,
  ]
  const hops = Math.floor(chain.length / 2)
  const otherKey = playerKey === 'p1' ? 'p2' : 'p1'
  const coStarActor = game?.[otherKey]?.actor ?? null
  const lastMove = game?.lastMove ?? null

  // ── Already-used IDs (both players' history) ─────────────────────────────
  const chainMovieIds = new Set(chain.filter(i => i.type === 'movie').map(i => i.id))
  const chainActorIds = new Set(chain.filter(i => i.type === 'actor').map(i => i.id))
  // startActor is always the first actor — also block it from re-selection
  chainActorIds.add(startActor.id)

  const filteredMovies = movieFilter.trim()
    ? movies.filter(m => m.title.toLowerCase().includes(movieFilter.toLowerCase()))
    : movies

  // ── Co-star abandoned — full-screen replacement ──────────────────────────
  if (abandonedByOther) {
    return (
      <div className="abandoned-screen">
        <div className="abandoned-screen__inner">
          <div className="abandoned-screen__icon" aria-hidden="true"><IconClapper size={48} /></div>
          <h2 className="abandoned-screen__title">Your co-star called cut.</h2>
          <p className="abandoned-screen__sub">The shoot has been cancelled.</p>
          <p className="abandoned-screen__refund"><IconTicket size={14} style={{ verticalAlign: 'middle' }} /> Your ticket has been refunded.</p>
          <button
            type="button"
            className="btn-primary abandoned-screen__btn"
            onClick={onExit}
          >
            Back to Menu
          </button>
        </div>
      </div>
    )
  }

  // ── Win screen — true full-screen, covers header + nav ──────────────────
  if (phase === 'won') {
    return (
      <div className="coop-win-screen">
        <div className="coop-win-screen__inner">
          <div className="coop-win-screen__trophy" aria-hidden="true"><IconTrophy size={48} /></div>
          <h2 className="coop-win-screen__title">You did it!</h2>
          <div className="coop-win-screen__stat">
            {hops} hop{hops !== 1 ? 's' : ''}
          </div>
          <div className="coop-win-screen__filmstrip-wrap">
            <Filmstrip chain={displayChain} hidePlaceholder />
          </div>
          <p className="coop-win-screen__wrap">That's a wrap! <IconClapper size={16} style={{ verticalAlign: 'middle' }} /></p>
          <p className="coop-win-screen__sub">
            You and your co-star connected the stars together
          </p>
          <button
            type="button"
            className="btn-primary coop-win-screen__btn"
            onClick={onExit}
          >
            Back to Menu
          </button>
        </div>
      </div>
    )
  }

  // ── Hard block: not this player's turn ──────────────────────────────────
  if (phase === 'waiting') {
    return (
      <div className="coop-waiting-screen">
        <div className="coop-goal-pill coop-goal-pill--waiting">
          <img
            src={goalActor.profile_path ? profileUrl(goalActor.profile_path, 'w185') : ACTOR_PH}
            alt={goalActor.name}
            className="coop-goal-pill__img"
            onError={e => { e.target.src = ACTOR_PH }}
          />
          <span className="coop-goal-pill__text">
            Goal: <strong>{goalActor.name}</strong>
          </span>
        </div>
        <div className="coop-waiting">
          {coStarActor?.profile_path && (
            <img
              src={profileUrl(coStarActor.profile_path, 'w185')}
              alt={coStarActor.name}
              className="coop-waiting__avatar"
              onError={e => { e.target.src = ACTOR_PH }}
            />
          )}
          <p className="coop-waiting__msg">Waiting on your co-star…</p>
          <p className="coop-waiting__hops">
            {hops} hop{hops !== 1 ? 's' : ''} so far
          </p>
        </div>
      </div>
    )
  }

  // ── Main gameplay screen ─────────────────────────────────────────────────
  return (
    <div className="coop-screen">

      {/* ── Toast ── */}
      {toast && <div className="coop-toast">{toast}</div>}

      {/* ── Chain bar — top strip ── */}
      <div className="coop-chain-bar">
        <div className="coop-chain-wrap">
          <Filmstrip chain={displayChain} hidePlaceholder />
          <div ref={chainEndRef} />
        </div>
        <div className="coop-goal-pill">
          <img
            src={goalActor.profile_path ? profileUrl(goalActor.profile_path, 'w185') : ACTOR_PH}
            alt={goalActor.name}
            className="coop-goal-pill__img"
            onError={e => { e.target.src = ACTOR_PH }}
          />
          <span className="coop-goal-pill__text">
            Goal: <strong>{goalActor.name}</strong>
          </span>
        </div>
      </div>

      {/* ── Controls bar — quit + hops stat ── */}
      <div className="coop-controls-bar">
        <button
          type="button"
          className="ctrl-pill ctrl-pill--btn ctrl-pill--danger"
          onClick={() => setQuitDialogOpen(true)}
        >
          <IconCut size={15} />Call Cut
        </button>
        <span className="ctrl-pill ctrl-pill--stat ctrl-pill--push">
          {hops} hop{hops !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Step nav — back button, always visible, only when movie selected ── */}
      {phase === 'my-turn' && selectedMovie && (
        <div className="coop-step-nav">
          <button
            type="button"
            className="btn-ghost coop-step-nav__back"
            onClick={handleBackToStep1}
          >
            ← Back
          </button>
          <span className="coop-step-nav__movie">{selectedMovie.title}</span>
        </div>
      )}

      {/* ── Scrollable body ── */}
      <div className="coop-body">

        {/* Init spinner */}
        {phase === 'init' && (
          <div className="coop-center">
            <div className="spinner" />
            <p className="coop-center__msg">Setting up…</p>
          </div>
        )}

        {/* My turn — step 1: pick a movie */}
        {phase === 'my-turn' && !selectedMovie && (
          <>
            {/* Actor context header */}
            <div className="coop-actor-header">
              <img
                src={currentActor.profile_path
                  ? profileUrl(currentActor.profile_path, 'w185')
                  : ACTOR_PH}
                alt={currentActor.name}
                className="coop-actor-header__img"
                onError={e => { e.target.src = ACTOR_PH }}
              />
              <div className="coop-actor-header__info">
                <p className="coop-actor-header__label">Your turn <IconClapper size={15} style={{ verticalAlign: 'middle' }} /> · Pick a movie starring</p>
                <p className="coop-actor-header__name">{currentActor.name}</p>
              </div>
            </div>

            {/* Movie search bar */}
            <div className="movie-filter-bar coop-filter-bar">
              <span className="movie-filter-bar__icon" aria-hidden="true">⌕</span>
              <input
                ref={movieFilterRef}
                className="movie-filter-bar__input"
                type="text"
                placeholder="Search movies…"
                value={movieFilter}
                onChange={e => setMovieFilter(e.target.value)}
                aria-label="Filter movies by title"
              />
              {movieFilter && (
                <>
                  <span className="movie-filter-bar__count">
                    {filteredMovies.length} / {movies.length}
                  </span>
                  <button
                    className="movie-filter-bar__clear"
                    onClick={() => { setMovieFilter(''); movieFilterRef.current?.focus() }}
                    aria-label="Clear filter"
                  ><IconX size={14} /></button>
                </>
              )}
            </div>

            {/* Movie grid */}
            {loadingMovies && (
              <div className="coop-loading"><div className="spinner" /></div>
            )}

            {!loadingMovies && filteredMovies.length > 0 && (
              <div className="movie-grid coop-movie-grid">
                {filteredMovies.map(movie => (
                  <FlipMovieCard
                    key={movie.id}
                    movie={movie}
                    onExpand={handleExpand}
                    isInChain={chainMovieIds.has(movie.id)}
                  />
                ))}
              </div>
            )}

            {!loadingMovies && movies.length > 0 && filteredMovies.length === 0 && (
              <p className="movie-filter-bar__empty">
                No movies match "<strong>{movieFilter}</strong>"
              </p>
            )}

            {!loadingMovies && movies.length === 0 && !toast && (
              <p className="coop-empty">No movies found for {currentActor.name}.</p>
            )}
          </>
        )}

        {/* My turn — step 2: pick an actor from the cast */}
        {phase === 'my-turn' && selectedMovie && (
          <>
            {cast.length === 0 && (
              <p className="coop-empty">No cast found for {selectedMovie.title}.</p>
            )}
            {cast.length > 0 && (
              <div className="actor-grid coop-actor-grid">
                {cast.map(actor => {
                  const isTarget = actor.id === goalActor.id
                  return (
                    <FlipActorCard
                      key={actor.id}
                      actor={actor}
                      onExpand={handleExpand}
                      isTarget={isTarget}
                      isInChain={!isTarget && chainActorIds.has(actor.id)}
                    />
                  )
                })}
              </div>
            )}
          </>
        )}

      </div>{/* end .coop-body */}

      {/* ── Lock-in bar — fixed, slides up when actor is selected ── */}
      {phase === 'my-turn' && (
        <div className={`coop-lockin-bar${selectedActor ? ' coop-lockin-bar--visible' : ''}`}>
          {selectedActor && (
            <>
              <div className="coop-lockin-bar__trail">
                <span className="coop-lockin-bar__movie">{selectedMovie?.title ?? '…'}</span>
                <span className="coop-lockin-bar__arrow">→</span>
                <span className="coop-lockin-bar__actor">{selectedActor.name}</span>
              </div>
              <div className="coop-lockin-bar__actions">
                <button
                  type="button"
                  className="btn-ghost coop-lockin-bar__back"
                  onClick={() => setSelectedActor(null)}
                >
                  ← Change
                </button>
                <button
                  type="button"
                  className="btn-primary coop-lockin-bar__lock"
                  onClick={handleLockIn}
                  disabled={submitting}
                >
                  {submitting ? 'Locking…' : <>Lock In <IconLock size={14} style={{ verticalAlign: 'middle' }} /></>}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Reveal overlay ── */}
      {phase === 'reveal' && lastMove && (
        <div className="coop-reveal-overlay">
          <div className="coop-reveal-card">
            <p className="coop-reveal-card__title">Your co-star's move:</p>
            <div className="coop-reveal-card__move">
              <div className="coop-reveal-card__node">
                <img
                  src={lastMove.movie?.poster_path
                    ? posterUrl(lastMove.movie.poster_path, 'w185')
                    : MOVIE_PH}
                  alt={lastMove.movie?.title}
                  className="coop-reveal-card__poster"
                  onError={e => { e.target.src = MOVIE_PH }}
                />
                <span>{lastMove.movie?.title}</span>
              </div>
              <span className="coop-reveal-card__arrow">→</span>
              <div className="coop-reveal-card__node">
                <img
                  src={lastMove.actor?.profile_path
                    ? profileUrl(lastMove.actor.profile_path, 'w185')
                    : ACTOR_PH}
                  alt={lastMove.actor?.name}
                  className="coop-reveal-card__profile"
                  onError={e => { e.target.src = ACTOR_PH }}
                />
                <span>{lastMove.actor?.name}</span>
              </div>
            </div>
            <button
              type="button"
              className="btn-primary coop-reveal-card__btn"
              onClick={handleAcknowledge}
            >
              Got it, my turn! <IconClapper size={15} style={{ verticalAlign: 'middle' }} />
            </button>
          </div>
        </div>
      )}

      {/* ── Card overlay (movie / actor zoom — same as single player) ── */}
      {expandedCard && (
        <CardOverlay
          type={expandedCard.type}
          data={expandedCard.data}
          startRect={expandedCard.rect}
          onSelect={handleOverlaySelect}
          onDismiss={handleOverlayDismiss}
          isTarget={expandedCard.type === 'actor' && expandedCard.data.id === goalActor.id}
        />
      )}

      {/* ── Quit ("Call Cut") confirmation dialog ── */}
      {quitDialogOpen && (() => {
        const activeEntry = savedGameId ? getSavedGameById(savedGameId) : null
        const refundLine = activeEntry?.refundEligible
          ? "You'll get your ticket back — no hops made yet."
          : "No refund — you've already made progress."
        return (
        <div className="quit-overlay" onClick={() => setQuitDialogOpen(false)}>
          <div className="quit-dialog" onClick={(e) => e.stopPropagation()}>
            <p className="quit-dialog__title"><IconCut size={18} />Call Cut?</p>
            <p className="quit-dialog__body">
              Leaving now will end the game for your co-star too.
            </p>
            <p className="quit-dialog__refund">
              <IconTicket size={13} /> {refundLine}
            </p>
            <div className="quit-dialog__actions">
              <button
                type="button"
                className="btn-ghost quit-dialog__stay"
                onClick={() => setQuitDialogOpen(false)}
              >
                Stay on Set
              </button>
              <button
                type="button"
                className="btn-primary quit-dialog__confirm"
                onClick={handleConfirmQuit}
              >
                Call Cut
              </button>
            </div>
          </div>
        </div>
        )
      })()}

    </div>
  )
}
